import { z } from 'zod';

import { MESSAGE_DATA_TYPES } from './discriminators';

/**
 * Reference data attached to a message.
 * Matches LiteratureReference in types/api.d.ts.
 */
export const referenceDataSchema = z.object({
  external_reference_id: z.string(),
  external_reference_type: z.enum(['pubmed', 'pmc', 'clinicaltrials', 'cochrane', 'web', 'other']),
  title: z.string(),
  authors: z.string(),
  journal: z.string(),
  // `null` for non-PubMed URLs or PubMed entries without a parseable
  // date. Legacy payloads on the wire may carry `0` as the historic
  // "unknown" sentinel; both shapes parse here.
  year: z.number().nullable(),
  url: z.string(),
  abstract_snippet: z.string(),
  relevance_score: z.number().optional(),
  reputation_score: z.number().optional(),
});

export type ReferenceData = z.infer<typeof referenceDataSchema>;

/**
 * Reference message data — used when sharing a literature reference.
 *
 * The retired backend auto-attach pipeline stored a reference on the same
 * message with type !== 'reference' (undefined or 'message_with_citation').
 * Historical messages with that shape are still on the wire, so the
 * read-path contribution below carries `reference` independently of the
 * `type` discriminator to keep them parsing.
 */
export const payloadSchema = z.object({
  type: z.literal(MESSAGE_DATA_TYPES.REFERENCE),
  reference: referenceDataSchema,
});

export type ReferenceMessageData = z.infer<typeof payloadSchema>;

export const attributesSchema = z.object({
  reference: referenceDataSchema.optional(),
});

export function createReferenceMessageData(reference: ReferenceData): ReferenceMessageData {
  return {
    type: MESSAGE_DATA_TYPES.REFERENCE,
    reference,
  };
}
